import { Button } from '~/sotano-kit/primitives/button'
import { InfoCircledIcon } from '@radix-ui/react-icons'
import * as React from 'react'
import { toast } from 'sonner'
import { Input } from '~/sotano-kit/primitives/input'

import { usePathname, useRouter } from 'next/navigation'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/sotano-kit/primitives/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import { useUserTeamsList } from '~/hooks-data/team/use-user-teams-list'
import { BaseHubAvatar } from '~/components/basehub-avatar'
import { useMutation } from '@tanstack/react-query'
import {
  PublicRepo,
  PublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import { ToggleState } from '~/hooks/use-toggle-state'
import { forkRepository } from '~/lib/fork/fork-repository-action'
import { isErrorLike } from '@basehub/utils/error'
import { Label } from '@radix-ui/react-label'
import { repoTemplateInputSchema } from '~/lib/fork/types'
import { useUserStore } from '~/app/_store/user-store'
import { IsPublicRadioGroup } from './repository-settings'
import { encodeNextParam } from '~/app/(mkt)/_index-redirector/server'
import { ForkIcon } from '../(team)/components/repositories/repository/fork-icon'
import { TabsStoreData } from '../(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import {
  useSelectedBranch,
  useSelectedBranchOrCommitQuery,
} from '~/lib/commits/use-selected-branch-or-commit'
import { Spinner2 } from '~/components/misc/spinner'
import { usePlaygroundCookie } from '~/lib/playground/use-playground-id'

export const ForkDialog = ({
  team: currentTeam,
  repository,
  toggleState,
  hideTrigger = false,
  children = (
    <Button size="small" className="relative z-20 pointer-events-auto">
      <ForkIcon width={14} height={14} />
      Fork
    </Button>
  ),
  queryParam,
}: {
  team: Pick<PublicTeam, 'id' | 'title' | 'slug'>
  repository: PublicRepo
  toggleState: ToggleState
  hideTrigger?: boolean
  children?: React.ReactNode
  queryParam?: string
}) => {
  const {
    data: { teams },
  } = useUserTeamsList()
  const router = useRouter()
  const pathname = usePathname()
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const [didFirstQueryParamSync, setDidFirstQueryParamSync] =
    React.useState(false)
  const branchOrCommit = useSelectedBranchOrCommitQuery().data
  const playgroundCookie = usePlaygroundCookie()
  const branch = useSelectedBranch().branch
  const isForkingPlayground =
    branch && ((!!playgroundCookie && branch.isDefault) || branch.playgroundId)

  const { mutate, isLoading, error, isError } = useMutation(
    async ({ formData }: { formData: FormData }) => {
      if (branchOrCommit?.playgroundId) {
        const response = await fetch('/api/duplicate-repo', {
          method: 'POST',
          body: JSON.stringify({
            teamSlug: currentTeam.slug,
            repoSlug: repository.slug,
            targetId: formData.get('targetTeamId'),
            title: formData.get('targetRepoName'),
            playgroundId: branchOrCommit?.playgroundId ?? playgroundCookie,
            visibility: formData.get('Visibility'),
            authorId: optimisticUser?.id,
          }),
        })

        if (!response.ok) {
          throw new Error('Failed to duplicate repository')
        }

        const data = (await response.json()) as any
        if (data?.error) {
          throw new Error(data.error)
        }

        return { data }
      }

      const response = await forkRepository(
        repoTemplateInputSchema.parse({
          sourceRepoId: repository.id,
          targetRepoName: formData.get('targetRepoName'),
          targetTeamId: formData.get('targetTeamId'),
          linkOriginalCommits: Boolean(formData.get('linkOriginalCommits')),
          sourceTeam: currentTeam,
          userId: optimisticUser?.id,
          visibility: formData.get('Visibility'),
          playgroundId: playgroundCookie,
        })
      )

      if (!response.success) {
        console.error(response.error)
        if (isErrorLike(response.error)) {
          throw new Error(response.error.message)
        }
        if (typeof response.error === 'string') {
          throw new Error(response.error)
        }
        throw new Error("Unknown error, couldn't duplicate repository")
      }

      return response
    },
    {
      onSuccess: ({ data: { newRepository } }, { formData }) => {
        toggleState.handleOff()
        toast.success('Initializing new repository')
        // redirect to duplicated repo
        const targetTeam = teams?.find(
          (team) => team.id === formData.get('targetTeamId')
        )
        if (targetTeam) {
          sessionStorage.setItem(
            `bshb-tabs-${newRepository.value.id}`,
            JSON.stringify({
              state: {
                tabs: [
                  {
                    key: 'dev:connect',
                    partiallyOpen: false,
                  },
                ],
                activeTab: {
                  key: 'dev:connect',
                  partiallyOpen: false,
                },
                forceActiveTabOntoURL: false,
              } satisfies Pick<
                TabsStoreData,
                'tabs' | 'forceActiveTabOntoURL' | 'activeTab'
              >,
            })
          )
          window.location.href = `/${targetTeam.slug}/${newRepository.value.slug}`
        }
      },
    }
  )

  const shouldRedirectWhenOpen = !teams.length
  const isRedirecting = toggleState.isOn && shouldRedirectWhenOpen

  React.useEffect(() => {
    if (isRedirecting) {
      router.push(
        `/login?next=${encodeNextParam(
          pathname + (queryParam ? `?${queryParam}=true` : '')
        )}`
      )
    }
  }, [isRedirecting, pathname, router, queryParam])

  React.useEffect(() => {
    if (queryParam) {
      const url = new URL(window.location.href)
      const value = url.searchParams.get(queryParam)
      if (value === 'true' || value === '1') {
        toggleState.handleOn()
      }
    }
    setDidFirstQueryParamSync(true)
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toggleState.handleOn is useCallback dude...
  }, [queryParam, pathname, router, toggleState.handleOn])

  React.useEffect(() => {
    if (queryParam && didFirstQueryParamSync && !isRedirecting) {
      const url = new URL(window.location.href)
      if (toggleState.isOn) {
        url.searchParams.set(queryParam, '1')
      } else {
        url.searchParams.delete(queryParam)
      }
      window.history.replaceState({}, '', url.toString())
    }
  }, [toggleState.isOn, didFirstQueryParamSync, queryParam, isRedirecting])

  const teamsList =
    [...teams].sort((a, b) =>
      (a.plan && a.plan === 'personal') || a.plan_v2?.type === 'personal'
        ? -1
        : a.title.localeCompare(b.title)
    ) ?? []

  return (
    <Dialog
      open={isRedirecting ? false : toggleState.isOn}
      onOpenChange={toggleState.handleToggle}
    >
      {!hideTrigger && (
        <DialogTrigger asChild>
          {children ?? (
            <Button
              size="small"
              className="relative z-20 pointer-events-auto"
              intent="secondary"
            >
              <ForkIcon width={14} height={14} />
              Fork
            </Button>
          )}
        </DialogTrigger>
      )}

      <DialogContent className="p-6">
        <DialogHeader>
          <DialogTitle>
            {isForkingPlayground
              ? 'Claim this playground'
              : `Fork “${repository.title}”`}
          </DialogTitle>
          <DialogDescription className="text-sm">
            Turn this playground into a repository in your team. Your current
            changes will be saved.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            mutate({ formData: new FormData(e.currentTarget) })
          }}
        >
          <div className="grid gap-y-4">
            {/* Repository name 📝 */}
            <FormField label="Repository name" htmlFor="targetRepoName">
              <Input
                id="targetRepoName"
                name="targetRepoName"
                size="xs"
                required
                defaultValue={repository.title}
              />
            </FormField>
            {/* Visibility 🔒 */}
            <FormField label="Visibility">
              <IsPublicRadioGroup />
            </FormField>
            {/* Repository destination 🛳️ */}
            <FormField label="Repository destination" htmlFor="targetTeamId">
              {/* Wrapper necessary to correctly position the select required popover */}
              <div className="relative [&>select]:left-1/2 [&>select]:bottom-0">
                <Select
                  required
                  name="targetTeamId"
                  defaultValue={teamsList[0]?.id}
                >
                  <SelectTrigger id="targetTeamId" aria-label="select team">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {teamsList.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        <div className="flex !flex-row items-center justify-between gap-2 text-right">
                          <BaseHubAvatar
                            id={team.id}
                            name={team.title}
                            size="xsmall"
                          />
                          <span>{team.title}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </FormField>
            {/* <LabeledCheckbox
              size="xs"
              horizontal="reverse"
              className="my-1.5 !gap-x-2"
              name="linkOriginalCommits"
              label="Keep original commits"
              checkboxProps={{ defaultChecked: true }}
            /> */}
            {isError && (
              <span className="flex w-full ml-2 text-xs leading-4 gap-x-1 text-dark-error">
                <InfoCircledIcon className="min-w-max" width={15} height={15} />
                {(error as Error).message}
              </span>
            )}
          </div>

          <DialogFooter divider>
            <DialogClose asChild>
              <Button intent="secondary">Cancel</Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={isLoading}
              icon={
                isLoading ? (
                  <Spinner2 className="size-[15px] text-inherit" />
                ) : null
              }
            >
              {isForkingPlayground ? 'Claim' : 'Fork'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export const FormField = ({
  label,
  children,
  ...props
}: {
  label: string
  children: React.ReactNode
} & React.ComponentProps<typeof Label>) => {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs text-dark-muted" {...props}>
        {label}
      </Label>
      {children}
    </div>
  )
}
